import { useRegisterSW } from 'virtual:pwa-register/react';
import { usePostHog } from 'posthog-js/react';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { PWA_EVENTS } from '@/lib/analytics/posthog.events';

/**
 * Interval in milliseconds to check for service worker updates
 * Default: 1 hour
 */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * UpdatePrompt component displays a toast notification when a new version
 * of the PWA is available. Users can choose to update immediately or dismiss.
 *
 * Features:
 * - Automatic service worker registration
 * - Periodic update checks (every hour)
 * - Non-blocking toast UI
 * - PostHog analytics tracking for update events
 */
export function UpdatePrompt() {
  const posthog = usePostHog();

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(registration) {
      console.log('[PWA] Service worker registered:', registration?.scope);

      // Set up periodic update checks
      if (registration) {
        setInterval(() => {
          console.log('[PWA] Checking for updates...');
          registration.update();
        }, UPDATE_CHECK_INTERVAL_MS);
      }
    },
    onRegisterError(error) {
      console.error('[PWA] Service worker registration error:', error);
      posthog?.capture(PWA_EVENTS.REGISTRATION_ERROR, {
        error: error.message,
      });
    },
    onNeedRefresh() {
      console.log('[PWA] New content available, update ready');
      posthog?.capture(PWA_EVENTS.UPDATE_AVAILABLE);
    },
  });

  const handleUpdate = () => {
    posthog?.capture(PWA_EVENTS.UPDATE_ACCEPTED);
    updateServiceWorker(true);
  };

  const handleDismiss = () => {
    posthog?.capture(PWA_EVENTS.UPDATE_DISMISSED);
    setNeedRefresh(false);
  };

  // Show toast for update available
  if (needRefresh) {
    return (
      <ToastNotification
        open={needRefresh}
        onClose={handleDismiss}
        title="Update Available"
        description="A new version of the app is ready. Reload to get the latest features."
        variant="success"
        position="bottom"
        actions={[
          {
            label: 'Update Now',
            onClick: handleUpdate,
            variant: 'default',
          },
          {
            label: 'Later',
            onClick: handleDismiss,
            variant: 'outline',
          },
        ]}
      />
    );
  }

  return null;
}

